"use client";

import { SectionBadge } from "./SectionBadge";
import { useI18n } from "@/i18n/context";

export function ExperienceSection() {
  const { t } = useI18n();

  return (
    <section id="experience" className="relative min-h-[1241px] overflow-hidden">
      {/* Badge */}
      <div className="relative z-10 pt-[85px]" data-reveal>
        <SectionBadge>{t.experience.badge}</SectionBadge>
      </div>

      {/* Title */}
      <h2 data-reveal className="relative z-10 font-clash font-bold text-[38px] text-text-primary text-center uppercase tracking-[2.66px] leading-normal mt-[40px] px-8">
        {t.experience.title}
      </h2>

      {/* Two-column split — slide inward */}
      <div className="relative z-10 flex mt-[40px]">
        {/* Left column: For You — slides from left */}
        <div data-reveal="left" className="w-1/2 bg-bg-primary relative">
          <ColumnHeader>{t.experience.forYou}</ColumnHeader>
          <div className="px-[60px] pt-[60px] pb-[80px]">
            <StepsList steps={t.experience.steps} />
          </div>
        </div>

        {/* Right column: Their Profile — slides from right */}
        <div data-reveal="right" className="w-1/2 bg-bg-warm relative">
          <ColumnHeader>{t.experience.theirProfile}</ColumnHeader>
          <div className="px-[60px] pt-[60px] pb-[80px]">
            <StepsList steps={t.experience.steps} />
          </div>
          {/* Sparkle decorations — scale in after columns land */}
          <Sparkle className="absolute top-[127px] left-[116px]" size={22} delay={300} />
          <Sparkle className="absolute top-[145px] right-[146px]" size={22} delay={400} />
          <Sparkle className="absolute top-[307px] left-[205px]" size={20} delay={500} />
          <Sparkle className="absolute top-[408px] right-[95px]" size={24} delay={600} />
          <Sparkle className="absolute top-[610px] left-[68px]" size={18} delay={700} />
          <Sparkle className="absolute top-[729px] right-[146px]" size={22} delay={800} />
          <Sparkle className="absolute right-[83px] top-[300px]" size={41} delay={350} />
        </div>
      </div>
    </section>
  );
}

function ColumnHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-[86px] border-b border-text-primary/10 flex items-center justify-center">
      <span className="font-jetbrains text-[18px] text-text-primary uppercase tracking-[1.26px]">
        {children}
      </span>
    </div>
  );
}

function StepsList({ steps }: { steps: string[] }) {
  return (
    <div className="font-besley text-[24px] text-text-secondary tracking-[1.68px] leading-[28px] text-center space-y-[16px]">
      <p className="font-clash font-bold text-[24px] text-text-primary uppercase tracking-[1.68px] leading-normal mb-[24px]">
        {steps[0]}
      </p>
      {steps.slice(1).map((step, i) => (
        <p key={i}>
          {i + 2}. {step}
        </p>
      ))}
    </div>
  );
}

function Sparkle({
  className = "",
  size = 22,
  delay = 0,
}: {
  className?: string;
  size?: number;
  delay?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      className={`${className} opacity-0 scale-[0.85] transition-all duration-1000`}
      style={{
        transitionDelay: `${delay}ms`,
        animation: `sparkle-in 1s ${delay}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
      }}
    >
      <path
        d="M11 0L13.09 8.91L22 11L13.09 13.09L11 22L8.91 13.09L0 11L8.91 8.91L11 0Z"
        fill="#222F40"
        opacity="0.15"
      />
    </svg>
  );
}
